import React, { useEffect, useMemo, useState } from 'react';
import useLeaderboard from '../hooks/useLeaderboard';

const PERIOD_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: 'week', label: 'Diese Woche' },
  { value: 'month', label: 'Diesen Monat' },
];

const BOARD_OPTIONS = [
  { value: 'xp', label: 'XP-Ranking' },
  { value: 'reporters', label: 'Pfandmelder' },
];

const VIEW_OPTIONS = [
  { value: 'top', label: 'Top 50' },
  { value: 'around_me', label: 'Rund um mich' },
];

const REPORTER_STATUS_OPTIONS = [
  { value: 'valid', label: 'Alle gültigen Meldungen' },
  { value: 'confirmed', label: 'Nur abgeholt/bestätigt' },
];

const REPORTER_LIMIT_OPTIONS = [
  { value: 10, label: 'Top 10' },
  { value: 50, label: 'Top 50' },
  { value: 100, label: 'Top 100' },
];

const ESTIMATE_NOTE = 'Die Werte sind geschätzte Mindestwerte. Pfand wird mit 0,08 € für Mehrweg, 0,25 € für Einweg und 0,33 € für gemischte Funde berechnet. Dinge „zu verschenken“ werden pauschal mit mindestens 1,00 € geschätzt. Tatsächliche Werte können abweichen.';

function formatNumber(value) {
  return new Intl.NumberFormat('de-DE').format(Number(value || 0));
}

function formatEuroCents(value) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(value || 0) / 100);
}

function getCrewLabel(crew) {
  if (!crew) {
    return 'Crew';
  }

  if (typeof crew.title === 'string' && crew.title.trim()) {
    return crew.title.trim();
  }

  if (crew.title && typeof crew.title === 'object' && typeof crew.title.rendered === 'string' && crew.title.rendered.trim()) {
    return crew.title.rendered.trim();
  }

  if (typeof crew.name === 'string' && crew.name.trim()) {
    return crew.name.trim();
  }

  return 'Crew';
}

function getProfileHref(row) {
  if (row?.is_me) {
    return '#/profile';
  }

  const slug = row?.profile_slug || row?.user_nicename || row?.user_login || '';
  if (!slug || row?.is_anonymous) {
    return null;
  }

  return `#/profil/${encodeURIComponent(slug)}`;
}

function LeaderboardIdentity({ row, period }) {
  const profileHref = getProfileHref(row);
  const username = row.username || row.display_name || 'Pfandpirat';
  const avatar = (
    <img
      className="leaderboard-row__avatar"
      src={row.avatar_url || 'https://app.pfandpirat.com/wp-content/uploads/Pfandpirat-7.jpg'}
      alt={username}
      loading="lazy"
    />
  );

  const nameBlock = (
    <>
      <div className="leaderboard-row__name">
        {username}
        {row.is_me ? <span className="leaderboard-chip leaderboard-chip--me">Du</span> : null}
      </div>
      <div className="leaderboard-row__meta">
        <span className="leaderboard-chip">Level {row.level}</span>
        {period !== 'all' ? <span>{formatNumber(row.xp_total)} Gesamt-XP</span> : null}
      </div>
    </>
  );

  return (
    <>
      {profileHref ? (
        <a className="leaderboard-row__avatar-link" href={profileHref} title={`Profil von ${username} öffnen`}>
          {avatar}
        </a>
      ) : (
        avatar
      )}
      <div className="leaderboard-row__identity">
        {profileHref ? (
          <a className="leaderboard-row__identity-link" href={profileHref} title={`Profil von ${username} öffnen`}>
            {nameBlock}
          </a>
        ) : (
          nameBlock
        )}
      </div>
    </>
  );
}

function ReporterIdentity({ row }) {
  const profileHref = getProfileHref(row);
  const displayName = row.display_name || 'Pfandpirat';
  const avatar = row.avatar_url ? (
    <img className="leaderboard-row__avatar" src={row.avatar_url} alt={displayName} loading="lazy" />
  ) : (
    <div className="leaderboard-row__avatar leaderboard-row__avatar--placeholder" aria-hidden="true">
      ♻
    </div>
  );

  const nameBlock = (
    <>
      <div className="leaderboard-row__name">
        {displayName}
        {row.is_me ? <span className="leaderboard-chip leaderboard-chip--me">Du</span> : null}
        {row.is_anonymous ? <span className="leaderboard-chip">anonym</span> : null}
      </div>
      <div className="leaderboard-row__meta">
        <span>{formatNumber(row.reports_count)} Meldungen</span>
        <span>{formatNumber(row.confirmed_reports_count)} abgeholt/bestätigt</span>
        {row.xp_total ? <span>{formatNumber(row.xp_total)} XP daraus</span> : null}
      </div>
    </>
  );

  return (
    <>
      {profileHref ? (
        <a className="leaderboard-row__avatar-link" href={profileHref} title={`Profil von ${displayName} öffnen`}>
          {avatar}
        </a>
      ) : (
        avatar
      )}
      <div className="leaderboard-row__identity">
        {profileHref ? (
          <a className="leaderboard-row__identity-link" href={profileHref} title={`Profil von ${displayName} öffnen`}>
            {nameBlock}
          </a>
        ) : (
          nameBlock
        )}
      </div>
    </>
  );
}

function LeaderboardRow({ row, period }) {
  const score = period === 'all' ? Number(row.xp_total || 0) : Number(row.xp_period || 0);

  return (
    <div className={`leaderboard-row${row.is_me ? ' leaderboard-row--me' : ''}`}>
      <div className="leaderboard-row__rank">#{row.rank}</div>
      <LeaderboardIdentity row={row} period={period} />
      <div className="leaderboard-row__score">
        <strong>{formatNumber(score)} XP</strong>
        {period !== 'all' ? <span>im Zeitraum</span> : <span>gesamt</span>}
      </div>
    </div>
  );
}

function ReporterLeaderboardRow({ row }) {
  return (
    <div className={`leaderboard-row leaderboard-row--reporter${row.is_me ? ' leaderboard-row--me' : ''}`}>
      <div className="leaderboard-row__rank">#{row.rank}</div>
      <ReporterIdentity row={row} />
      <div className="leaderboard-row__score">
        <strong>{formatEuroCents(row.estimated_value_cents)}</strong>
        <span>geschätzter Mindestwert</span>
      </div>
    </div>
  );
}

function ReporterSummary({ summary }) {
  const safeSummary = summary || {};
  return (
    <div className="leaderboard-reporter-summary" aria-label="Zusammenfassung Pfandmelder">
      <div>
        <strong>{formatNumber(safeSummary.reports_count)}</strong>
        <span>Meldungen</span>
      </div>
      <div>
        <strong>{formatNumber(safeSummary.confirmed_reports_count)}</strong>
        <span>abgeholt/bestätigt</span>
      </div>
      <div>
        <strong>{formatEuroCents(safeSummary.estimated_value_cents)}</strong>
        <span>geschätzter Wert</span>
      </div>
    </div>
  );
}

export default function LeaderboardPage({ user, crews = [], crewId, setCrewId }) {
  const [board, setBoard] = useState('xp');
  const [scope, setScope] = useState('global');
  const [period, setPeriod] = useState('all');
  const [city, setCity] = useState('');
  const [view, setView] = useState('top');
  const [reportStatus, setReportStatus] = useState('valid');
  const [reportLimit, setReportLimit] = useState(50);

  const isReporterBoard = board === 'reporters';
  const accessibleCrews = useMemo(() => {
    return (Array.isArray(crews) ? crews : []).filter(
      crew => crew && (crew.can_view_private_data || crew.is_member || crew.is_owner)
    );
  }, [crews]);

  useEffect(() => {
    if (!isReporterBoard && scope === 'crew' && !crewId && accessibleCrews.length && typeof setCrewId === 'function') {
      setCrewId(accessibleCrews[0].id);
    }
  }, [isReporterBoard, scope, crewId, accessibleCrews, setCrewId]);

  const leaderboardParams = useMemo(() => {
    if (isReporterBoard) {
      return {
        board: 'reporters',
        period,
        report_status: reportStatus,
        limit: reportLimit,
      };
    }

    return {
      board: 'xp',
      scope,
      period,
      city,
      view,
      crew_id: scope === 'crew' ? crewId : undefined,
      limit: view === 'around_me' ? 7 : 50,
    };
  }, [isReporterBoard, period, reportStatus, reportLimit, scope, city, view, crewId]);

  const { items, my_entry, loading, error, reload, cities = [], summary, estimate_note: estimateNote } = useLeaderboard(
    leaderboardParams,
    {
      enabled: isReporterBoard || scope !== 'crew' || Boolean(crewId),
    }
  );

  const cityOptions = useMemo(
    () => (Array.isArray(cities) ? cities : []).filter(option => option?.value && option?.label),
    [cities]
  );

  useEffect(() => {
    if (isReporterBoard || !city || loading) {
      return;
    }

    if (!cityOptions.some(option => option.value === city)) {
      setCity('');
    }
  }, [isReporterBoard, city, cityOptions, loading]);

  const emptyCrewState = !isReporterBoard && scope === 'crew' && !accessibleCrews.length;
  const noCrewSelected = !isReporterBoard && scope === 'crew' && accessibleCrews.length > 0 && !crewId;
  const emptyMessage = isReporterBoard
    ? 'Noch keine Pfandmeldungen in diesem Zeitraum vorhanden.'
    : city
      ? 'Für diesen Ort gibt es noch keine Einträge.'
      : period === 'all'
        ? 'Noch keine XP-Daten für das Leaderboard vorhanden.'
        : 'Für diesen Zeitraum sind noch keine XP-Ereignisse vorhanden.';
  const myEntryAlreadyVisible = isReporterBoard
    ? items.some(row => row.is_me)
    : items.some(row => Number(row.user_id) === Number(user?.id));

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-card">
        <div className="leaderboard-header">
          <div>
            <h1>🏆 Leaderboard</h1>
            <p>{isReporterBoard ? 'Wer macht die meisten Pfandschätze sichtbar?' : 'Vergleiche deinen XP-Stand mit anderen Pfandpiraten.'}</p>
          </div>
          <button type="button" className="leaderboard-refresh" onClick={reload}>
            Aktualisieren
          </button>
        </div>

        <div className="leaderboard-board-tabs" aria-label="Leaderboard auswählen">
          {BOARD_OPTIONS.map(option => (
            <button
              key={option.value}
              type="button"
              className={`leaderboard-board-tab${board === option.value ? ' is-active' : ''}`}
              onClick={() => setBoard(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="leaderboard-filters">
          {isReporterBoard ? null : (
            <div className="leaderboard-filter-group">
              <label>Ansicht</label>
              <div className="leaderboard-toggle-row">
                <button
                  type="button"
                  className={`leaderboard-toggle${scope === 'global' ? ' is-active' : ''}`}
                  onClick={() => setScope('global')}
                >
                  Global
                </button>
                <button
                  type="button"
                  className={`leaderboard-toggle${scope === 'crew' ? ' is-active' : ''}`}
                  onClick={() => setScope('crew')}
                  disabled={!accessibleCrews.length}
                >
                  Meine Crew
                </button>
              </div>
            </div>
          )}

          <div className="leaderboard-filter-group">
            <label htmlFor="leaderboard-period">Zeitraum</label>
            <select id="leaderboard-period" value={period} onChange={event => setPeriod(event.target.value)}>
              {PERIOD_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {isReporterBoard ? null : (
            <div className="leaderboard-filter-group">
              <label htmlFor="leaderboard-city">Ort / Stadt</label>
              <select id="leaderboard-city" value={city} onChange={event => setCity(event.target.value)}>
                <option value="">Alle Orte</option>
                {cityOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="leaderboard-filter-group">
            <label htmlFor={isReporterBoard ? 'leaderboard-report-limit' : 'leaderboard-view'}>Liste</label>
            {isReporterBoard ? (
              <select id="leaderboard-report-limit" value={reportLimit} onChange={event => setReportLimit(Number(event.target.value) || 50)}>
                {REPORTER_LIMIT_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <select id="leaderboard-view" value={view} onChange={event => setView(event.target.value)}>
                {VIEW_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          {isReporterBoard ? (
            <div className="leaderboard-filter-group">
              <label htmlFor="leaderboard-report-status">Status</label>
              <select id="leaderboard-report-status" value={reportStatus} onChange={event => setReportStatus(event.target.value)}>
                {REPORTER_STATUS_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {!isReporterBoard && scope === 'crew' ? (
            <div className="leaderboard-filter-group">
              <label htmlFor="leaderboard-crew">Crew</label>
              <select
                id="leaderboard-crew"
                value={crewId || ''}
                onChange={event => setCrewId?.(Number(event.target.value) || null)}
                disabled={!accessibleCrews.length}
              >
                {!accessibleCrews.length ? <option value="">Keine Crew verfügbar</option> : null}
                {accessibleCrews.map(crew => (
                  <option key={crew.id} value={crew.id}>
                    {getCrewLabel(crew)}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>

        {isReporterBoard ? (
          <>
            <ReporterSummary summary={summary} />
            <div className="leaderboard-estimate-note">{estimateNote || ESTIMATE_NOTE}</div>
          </>
        ) : null}

        {my_entry && !myEntryAlreadyVisible ? (
          <div className="leaderboard-my-row">
            <div className="leaderboard-my-row__label">{isReporterBoard ? 'Dein Meldebeitrag' : 'Dein Eintrag'}</div>
            {isReporterBoard ? (
              <ReporterLeaderboardRow row={{ ...my_entry, is_me: true }} />
            ) : (
              <LeaderboardRow row={{ ...my_entry, is_me: true }} period={period} />
            )}
          </div>
        ) : null}

        {emptyCrewState ? <div className="leaderboard-state">Du bist aktuell in keiner freigegebenen Crew.</div> : null}
        {noCrewSelected ? <div className="leaderboard-state">Wähle zuerst eine Crew aus.</div> : null}
        {loading ? <div className="leaderboard-state">Leaderboard wird geladen…</div> : null}
        {error ? (
          <div className="leaderboard-state leaderboard-state--error">
            {error.message || 'Leaderboard konnte nicht geladen werden.'}
          </div>
        ) : null}
        {!loading && !error && !emptyCrewState && !noCrewSelected && items.length === 0 ? (
          <div className="leaderboard-state">{emptyMessage}</div>
        ) : null}

        {!loading && !error && items.length > 0 ? (
          <div className="leaderboard-list">
            {items.map(row => (
              isReporterBoard ? (
                <ReporterLeaderboardRow key={`${row.rank}-${row.display_name || 'reporter'}`} row={row} />
              ) : (
                <LeaderboardRow key={`${row.user_id}-${row.rank}`} row={row} period={period} />
              )
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
